// src/pages/impots/EcheancesTaxPage/CollectePage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Container,
  ProgressBar,
  InputGroup,
  Toast,
  ToastContainer,
  Modal
} from "react-bootstrap";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import axios from "axios";
import {
  ArrowLeft,
  Cash,
  CheckCircle,
  InfoCircle,
  Phone,
  FileText,
  ExclamationTriangle,
  CreditCard2Front,
  PhoneVibrate,
  Bank2,
  Percent,
  HourglassSplit,
  CashCoin,
  CurrencyDollar,
  Receipt,
  Building,
  Briefcase
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";



const CollectePage = () => {
  const { taxId, contribuableId, periodId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultCurrency = sessionStorage.getItem("default_currency") || 'XOF';

  // États pour les données
  const [period, setPeriod] = useState(null);
  const [tax, setTax] = useState(null);
  const [contribuable, setContribuable] = useState(null);
  const [subAccount, setSubAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPeriod, setLoadingPeriod] = useState(true);

  // États pour les devises
  const [selectedCurrency, setSelectedCurrency] = useState(defaultCurrency);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [conversionRates, setConversionRates] = useState({});
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  // États pour le paiement
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentType, setPaymentType] = useState("full");
  const [amount, setAmount] = useState(0);
  const [transactionRef, setTransactionRef] = useState("");
  const [collecting, setCollecting] = useState(false);
  const [customAmount, setCustomAmount] = useState("");

  // États pour les modes de paiement spécifiques
  const [chequeNumber, setChequeNumber] = useState("");
  const [chequeBank, setChequeBank] = useState("");
  const [transferNumber, setTransferNumber] = useState("");
  const [transferBank, setTransferBank] = useState("");
  const [cardNumber, setCardNumber] = useState("");

  // États pour la détection réseau
  const [numeroPaiement, setNumeroPaiement] = useState("");
  const [rechercheLoading, setRechercheLoading] = useState(false);
  const [networkName, setNetworkName] = useState("");
  const [networkLogo, setNetworkLogo] = useState("");
  const [networkCode, setNetworkCode] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [walletsSupplementaires, setWalletsSupplementaires] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState("");
  const [detectionError, setDetectionError] = useState("");

  // États pour les notifications
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [convertingCurrency, setConvertingCurrency] = useState(false);
  
  // États pour la confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Mapping des activités
  const [activityMapping, setActivityMapping] = useState({});

  // Méthodes de paiement disponibles
  const paymentMethods = [
    { id: "cash", label: "Espèces", icon: <Cash className="me-2" />, color: "success", description: "Paiement en espèces", needsDetails: false },
    { id: "mobile_money", label: "Mobile Money", icon: <PhoneVibrate className="me-2" />, color: "primary", description: "Orange Money, Wave, etc.", needsDetails: true, detailType: "phone" },
    { id: "credit_card", label: "Carte Bancaire", icon: <CreditCard2Front className="me-2" />, color: "warning", description: "Visa, Mastercard", needsDetails: true, detailType: "card" },
    { id: "check", label: "Chèque", icon: <FileText className="me-2" />, color: "info", description: "Paiement par chèque bancaire", needsDetails: true, detailType: "cheque" },
    { id: "bank_transfer", label: "Virement bancaire", icon: <Bank2 className="me-2" />, color: "dark", description: "Virement depuis un compte bancaire", needsDetails: true, detailType: "transfer" }
  ];

  // Types de paiement
  const paymentTypes = [
    { id: "full", label: "Collecte complet", icon: <CheckCircle className="me-2" />, color: "success", description: "Collecter le montant total de l'échéance" },
    { id: "partial", label: "Collecte partiel", icon: <Percent className="me-2" />, color: "warning", description: "Collecter seulement une partie du montant" }
  ];

  const getAuthToken = () => sessionStorage.getItem("authToken");

  const getHeaders = useCallback(() => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }, []);

  const showToastMessage = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  const formatCurrency = (amount, currency = defaultCurrency) => {
    const numAmount = parseFloat(amount) || 0;
    return numAmount.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }) + ' ' + currency;
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Date invalide";
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString || "Non spécifiée";
    }
  };

  // ==================== FONCTIONS ACTIVITÉS ET SOUS-COMPTES ====================

  const fetchActivityMapping = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/activites?has_tax=yes`, { headers: getHeaders() });
      let activities = [];
      if (response.data && Array.isArray(response.data)) activities = response.data;
      else if (response.data && response.data.data && Array.isArray(response.data.data)) activities = response.data.data;
      
      const mapping = {};
      activities.forEach(act => {
        const id = act.id_export || act.code_category || act.id;
        const name = act.name || act.cat_name || `Activité ${id}`;
        mapping[id] = name;
        if (act.code_category && act.code_category !== id) {
          mapping[act.code_category] = name;
        }
      });
      setActivityMapping(mapping);
      return mapping;
    } catch (error) {
      console.error("Erreur chargement mapping activités:", error);
      return {};
    }
  };

  const getActivityName = (activityId) => {
    if (!activityId) return 'Non spécifiée';
    return activityMapping[activityId] || activityId;
  };

  const fetchSubAccounts = async (mainAccountId) => {
    if (!mainAccountId) return [];
    try {
      const response = await axios.get(`${API_BASE_URL}/taxpayers`, { headers: getHeaders() });
      let allTaxpayers = [];
      if (response.data && Array.isArray(response.data)) allTaxpayers = response.data;
      else if (response.data && response.data.data && Array.isArray(response.data.data)) allTaxpayers = response.data.data;
      
      return allTaxpayers.filter(
        taxpayer => taxpayer.main_account_id && taxpayer.main_account_id.toString() === mainAccountId.toString()
      );
    } catch (error) {
      console.error("Erreur chargement sous-comptes:", error);
      return [];
    }
  };

  // ==================== CHARGEMENT DES DONNÉES ====================

 const fetchPeriodData = async () => {
  console.log("=== PARAMÈTRES REÇUS ===");
  console.log("periodId (param URL):", periodId);
  console.log("taxId:", taxId);
  console.log("contribuableId:", contribuableId);
  console.log("======================");
  
  try {
    setLoadingPeriod(true);
    
    if (location.state?.period) {
      const periodData = location.state.period;
      setPeriod(periodData);
      setTax(location.state.tax || null);
      setContribuable(location.state.contribuable || null);
      setSubAccount(location.state.subAccount || null);
      setLoadingPeriod(false);
      setLoading(false);
      return;
    }

    await fetchActivityMapping();

    let foundPeriod = null;
    let foundContribuable = null;
    let foundSubAccount = null;

    // 1. Vérifier d'abord si l'ID est un compte principal
    try {
      const mainResponse = await axios.get(
        `${API_BASE_URL}/terms?usager_id=${contribuableId}`,
        { headers: getHeaders() }
      );
      
      if (mainResponse.data.code === 200 && mainResponse.data.echeances?.length > 0) {
        // PRIORITÉ ABSOLUE: Chercher par echeance_id d'abord
        let matchingEcheance = null;
        
        if (periodId) {
          matchingEcheance = mainResponse.data.echeances.find(e => 
            e.echeance_id === periodId || e.id?.toString() === periodId
          );
        }
        
        // Si non trouvé par echeance_id, chercher par taxe_id
        if (!matchingEcheance && taxId) {
          matchingEcheance = mainResponse.data.echeances.find(e => 
            e.taxe_id?.toString() === taxId?.toString()
          );
        }
        
        // Si toujours pas trouvé, prendre la première (fallback)
        if (!matchingEcheance && mainResponse.data.echeances.length > 0) {
          console.warn("Aucune correspondance exacte, prise de la première échéance");
          matchingEcheance = mainResponse.data.echeances[0];
        }
        
        if (matchingEcheance) {
          foundPeriod = matchingEcheance;
          console.log("✅ Échéance trouvée dans compte principal:", foundPeriod.echeance_id);
          
          const contribuableResponse = await axios.get(
            `${API_BASE_URL}/taxpayers/${contribuableId}`,
            { headers: getHeaders() }
          );
          foundContribuable = contribuableResponse.data;
        } else {
          console.log("Aucune échéance correspondante dans le compte principal");
        }
      }
    } catch (err) {
      console.log("Compte principal non trouvé ou sans échéances:", err);
    }

    // 2. Si pas trouvé, chercher parmi les sous-comptes
    if (!foundPeriod) {
      const subAccounts = await fetchSubAccounts(contribuableId);
      console.log("Recherche dans", subAccounts.length, "sous-comptes");
      
      for (const sub of subAccounts) {
        try {
          const subResponse = await axios.get(
            `${API_BASE_URL}/terms?usager_id=${sub.contribuable_id}`,
            { headers: getHeaders() }
          );
          
          if (subResponse.data.code === 200 && subResponse.data.echeances?.length > 0) {
            // PRIORITÉ ABSOLUE: Chercher par echeance_id d'abord
            let matchingEcheance = null;
            
            if (periodId) {
              matchingEcheance = subResponse.data.echeances.find(e => 
                e.echeance_id === periodId || e.id?.toString() === periodId
              );
            }
            
            // Si non trouvé par echeance_id, chercher par taxe_id
            if (!matchingEcheance && taxId) {
              matchingEcheance = subResponse.data.echeances.find(e => 
                e.taxe_id?.toString() === taxId?.toString()
              );
            }
            
            if (matchingEcheance) {
              foundPeriod = matchingEcheance;
              foundSubAccount = sub;
              foundContribuable = sub;
              console.log("✅ Échéance trouvée dans sous-compte:", foundPeriod.echeance_id);
              break;
            }
          }
        } catch (err) {
          console.error(`Erreur chargement échéances sous-compte ${sub.contribuable_id}:`, err);
        }
      }
    }

    // 3. Si toujours pas trouvé, essayer directement avec l'écheance_id
    if (!foundPeriod && periodId) {
      try {
        console.log("Recherche directe par echeance_id:", periodId);
        const directResponse = await axios.get(
          `${API_BASE_URL}/terms?echeance_id=${periodId}`,
          { headers: getHeaders() }
        );
        
        if (directResponse.data.code === 200 && directResponse.data.echeances?.length > 0) {
          foundPeriod = directResponse.data.echeances[0];
          console.log("✅ Échéance trouvée par recherche directe:", foundPeriod.echeance_id);
          
          const usagerId = foundPeriod.usager_id;
          const taxpayerResponse = await axios.get(
            `${API_BASE_URL}/taxpayers/${usagerId}`,
            { headers: getHeaders() }
          );
          foundContribuable = taxpayerResponse.data;
          
          if (foundContribuable.main_account_id) {
            foundSubAccount = foundContribuable;
          }
        } else {
          console.log("Recherche directe: aucune échéance trouvée");
        }
      } catch (err) {
        console.error("Erreur recherche directe:", err);
      }
    }

    if (foundPeriod) {
      const dueDate = new Date(foundPeriod.date_echeance);
      const today = new Date();
      const isOverdue = dueDate < today;
      
      const montantInitial = parseFloat(foundPeriod.montant_initial || foundPeriod.montant_attendu || "0") || 0;
      const montantPaye = parseFloat(foundPeriod.montant_paye || "0") || 0;
      const penalite = parseFloat(foundPeriod.penalite || "0") || 0;
      const valeurPenalites = parseFloat(foundPeriod.valeur_penalites || "0") || 0;
      const totalPenalite = Math.max(penalite, valeurPenalites);
      const interestAmount = isOverdue ? totalPenalite : 0;
      const montantTotalDu = montantInitial + interestAmount;
      const montantRestant = Math.max(0, montantTotalDu - montantPaye);
      const paymentPercentage = montantTotalDu > 0 ? Math.round((montantPaye / montantTotalDu) * 100) : 0;
      
      const getMonthName = (date) => {
        const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
          'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
        return monthNames[date.getMonth()];
      };
      
      const transformedPeriod = {
        id: foundPeriod.id?.toString(),
        taxe_id: foundPeriod.taxe_id,
        echeance_id: foundPeriod.echeance_id || foundPeriod.id?.toString(),
        usager_id: foundPeriod.usager_id,
        period_label: `${getMonthName(dueDate)} ${dueDate.getFullYear()}`,
        montant_initial: montantInitial,
        montant_paye: montantPaye,
        montant_restant: montantRestant,
        payment_percentage: paymentPercentage,
        due_date: foundPeriod.date_echeance,
        year: dueDate.getFullYear(),
        month: dueDate.getMonth() + 1,
        days_late: isOverdue ? Math.floor((today - dueDate) / (1000 * 60 * 60 * 24)) : 0,
        interest: interestAmount,
        penalite: penalite,
        valeur_penalites: valeurPenalites
      };
      
      setPeriod(transformedPeriod);
      setSubAccount(foundSubAccount);
      
      let taxName = "Taxe Non Spécifiée";
      if (foundPeriod.taxe) {
        taxName = foundPeriod.taxe.titre || foundPeriod.taxe.name;
      } else if (location.state?.tax?.name) {
        taxName = location.state.tax.name;
      }
      
      setTax({
        id: foundPeriod.taxe_id,
        name: taxName,
        code: `TX-${foundPeriod.taxe_id}`,
        frequency: foundPeriod.taxe?.frequence || "quarterly",
        montant_base: parseFloat(foundPeriod.taxe?.montant || "0"),
        description: foundPeriod.taxe?.description || ""
      });
      
      setContribuable(foundContribuable);
      
      console.log("=== Période chargée ===");
      console.log("periodId reçu:", periodId);
      console.log("echeance_id utilisé:", transformedPeriod.echeance_id);
      console.log("montant_restant:", montantRestant);
      console.log("======================");
      
    } else {
      console.error("❌ Aucune échéance trouvée!");
      showToastMessage("Aucune échéance trouvée pour ce contribuable", "error");
    }
    
  } catch (err) {
    console.error('Erreur chargement période:', err);
    showToastMessage(`Erreur lors du chargement des données: ${err.message}`, "error");
  } finally {
    setLoadingPeriod(false);
    setLoading(false);
  }
};



  // const fetchCurrenciesByCountry = async () => {
  //   try {
  //     setLoadingCurrencies(true);
  //     const countryCode = sessionStorage.getItem("country_code") || "GA";
  //     const sessionCurrency = sessionStorage.getItem("default_currency") || "XOF";
      
  //     try {
  //       const response = await axios.get(`${API_BASE_URL}/currencies/${countryCode}`, {
  //         headers: getHeaders(),
  //         timeout: 5000
  //       });
        
  //       if (response.data && Array.isArray(response.data) && response.data.length > 0) {
  //         const currencies = response.data.map(curr => ({
  //           code: curr.currency,
  //           name: getCurrencyName(curr.currency),
  //           symbol: getCurrencySymbol(curr.currency),
  //           is_default: curr.default === 0,
  //           exchange_rate: 1
  //         }));
          
  //         setAvailableCurrencies(currencies);
  //         const rates = {};
  //         currencies.forEach(curr => { rates[curr.code] = 1; });
  //         setConversionRates(rates);
          
  //         const defaultCurr = currencies.find(c => c.is_default) || currencies[0];
  //         if (defaultCurr) setSelectedCurrency(defaultCurr.code);
  //         return;
  //       }
  //     } catch (apiError) {
  //       console.warn("API /currencies non disponible:", apiError.message);
  //     }
      
  //     setAvailableCurrencies([{
  //       code: sessionCurrency,
  //       name: getCurrencyName(sessionCurrency),
  //       symbol: getCurrencySymbol(sessionCurrency),
  //       is_default: true,
  //       exchange_rate: 1
  //     }]);
  //     setConversionRates({ [sessionCurrency]: 1 });
  //     setSelectedCurrency(sessionCurrency);
      
  //   } catch (error) {
  //     console.error("Erreur chargement devises:", error);
  //     const sessionCurrency = sessionStorage.getItem("default_currency") || "XOF";
  //     setAvailableCurrencies([{
  //       code: sessionCurrency,
  //       name: getCurrencyName(sessionCurrency),
  //       symbol: getCurrencySymbol(sessionCurrency),
  //       is_default: true,
  //       exchange_rate: 1
  //     }]);
  //     setConversionRates({ [sessionCurrency]: 1 });
  //     setSelectedCurrency(sessionCurrency);
  //   } finally {
  //     setLoadingCurrencies(false);
  //   }
  // };

  const getCurrencyName = (currencyCode) => {
    const names = {
      'XAF': 'Franc CFA (CEMAC)',
      'XOF': 'Franc CFA (UEMOA)',
      'USD': 'Dollar US',
      'EUR': 'Euro',
      'GBP': 'Livre Sterling',
      'CAD': 'Dollar Canadien',
      'CHF': 'Franc Suisse'
    };
    return names[currencyCode] || currencyCode;
  };

  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'XAF': 'FCFA',
      'XOF': 'FCFA',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'C$',
      'CHF': 'CHF'
    };
    return symbols[currencyCode] || currencyCode;
  };

  const convertCurrency = async (fromCurrency, toCurrency, amountToConvert) => {
    if (fromCurrency === toCurrency) return amountToConvert;
    if (!amountToConvert || isNaN(amountToConvert)) return 0;
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/convert`,
        { from: fromCurrency, to: toCurrency, amount: amountToConvert },
        { headers: getHeaders(), timeout: 10000 }
      );
      
      if (response.data && response.data.result !== undefined) {
        const rate = parseFloat(response.data.rate) || (response.data.result / amountToConvert);
        setConversionRates(prev => ({ ...prev, [toCurrency]: rate }));
        return response.data.result;
      }
      
      if (response.data && response.data.converted_amount !== undefined) {
        const rate = response.data.converted_amount / amountToConvert;
        setConversionRates(prev => ({ ...prev, [toCurrency]: rate }));
        return response.data.converted_amount;
      }
      
      throw new Error("Format de réponse invalide");
      
    } catch (error) {
      console.log("Erreur conversion via API:", error);
      setConversionRates(prev => ({ ...prev, [toCurrency]: 1 }));
      showToastMessage(`⚠️ Conversion ${fromCurrency} → ${toCurrency} non disponible, taux 1:1 appliqué`, "warning");
      return amountToConvert;
    }
  };

  const convertAmountSync = (amountToConvert, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amountToConvert;
    if (!amountToConvert || isNaN(amountToConvert)) return 0;
    const rate = conversionRates[toCurrency];
    if (!rate) return amountToConvert;
    return Math.round(amountToConvert * rate * 100) / 100;
  };

  const detecterReseauMobile = async (phoneNumber) => {
    setRechercheLoading(true);
    setDetectionError("");
    const countryCodeSession = sessionStorage.getItem("country_code") || "GA";
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/payments/network?msisdn=${phoneNumber}&country=${countryCodeSession}&type=mobile_money&source=bo`,
        { headers: getHeaders() }
      );

      const data = response.data;

      if (response.status === 200 && data.statusRequest === 200) {
        setNetworkName(data.mobile_money_svce || data.network);
        setNetworkLogo(data.mobile_money_logo);
        setNetworkCode(data.mobile_money_code);
        setCountryCode(data.country);

        const wallets = [];
        const hasWallet = (data.has_wallet_1 || '').toString().toLowerCase() === 'yes';
        
        if (hasWallet) {
          wallets.push({
            code: data.mobile_money_code,
            name: data.mobile_money_svce || data.network,
            logo: data.mobile_money_logo,
            type: 'principal'
          });
          wallets.push({
            code: data.wallet_code,
            name: data.wallet_name_1,
            logo: data.wallet_logo_1,
            type: 'supplementaire'
          });
        } else {
          wallets.push({
            code: data.mobile_money_code,
            name: data.mobile_money_svce || data.network,
            logo: data.mobile_money_logo,
            type: 'principal'
          });
        }
        
        setWalletsSupplementaires(wallets);
        setSelectedWallet(wallets[0]?.code || "");
        setDetectionError("");
      } else {
        setDetectionError(data.message || 'Réseau non trouvé.');
      }
    } catch (error) {
      setDetectionError(error.response?.data?.message || 'Erreur de connexion.');
    } finally {
      setRechercheLoading(false);
    }
  };

  const calculateAmount = () => {
    if (!period) return 0;
    if (paymentType === "full") {
      return period.montant_restant || 0;
    } else {
      const parsedAmount = parseFloat(customAmount) || 0;
      const maxAmount = period.montant_restant || 0;
      return Math.min(Math.max(parsedAmount, 0), maxAmount);
    }
  };

  useEffect(() => {
    const calculatedAmount = calculateAmount();
    setAmount(calculatedAmount);
  }, [paymentType, customAmount, period]);

  useEffect(() => {
    const numeroNettoye = numeroPaiement.replace(/\s/g, '');
    if (numeroNettoye.length >= 7) {
      detecterReseauMobile(numeroPaiement);
    } else {
      setNetworkName("");
      setNetworkLogo("");
      setNetworkCode("");
      setCountryCode("");
      setWalletsSupplementaires([]);
      setSelectedWallet("");
      setDetectionError("");
    }
  }, [numeroPaiement]);

  useEffect(() => {
    setChequeNumber("");
    setChequeBank("");
    setTransferNumber("");
    setTransferBank("");
    setCardNumber("");
    setTransactionRef("");
  }, [paymentMethod]);

  useEffect(() => {
    fetchPeriodData();
    // fetchCurrenciesByCountry();
  }, [periodId, taxId, contribuableId, location.state]);

  const isPaymentDetailsValid = () => {
    switch (paymentMethod) {
      case "mobile_money":
        return transactionRef && transactionRef.length >= 7;
      case "credit_card":
        const cleanCardNumber = cardNumber.replace(/\s/g, '');
        return cleanCardNumber.length >= 13 && cleanCardNumber.length <= 19;
      case "check":
        return chequeNumber.trim() !== "" && chequeBank.trim() !== "";
      case "bank_transfer":
        return transferNumber.trim() !== "" && transferBank.trim() !== "";
      case "cash":
        return true;
      default:
        return true;
    }
  };

  // ==================== CONFIRMATION PAIEMENT - CORRECTION ICI ====================
  
  const confirmPayment = async () => {
    try {
      setCollecting(true);
      
      if (!period) {
        showToastMessage("Veuillez sélectionner une période", "error");
        return;
      }
      
      if (!isPaymentDetailsValid()) {
        showToastMessage("Veuillez remplir tous les champs requis pour ce mode de paiement", "error");
        return;
      }
      
      if (paymentType === "partial" && (!customAmount || parseFloat(customAmount) <= 0)) {
        showToastMessage("Veuillez saisir un montant valide pour le paiement partiel", "error");
        return;
      }
      
      const sessionCountryCode = sessionStorage.getItem("country_code") || "GA";
      
      let finalCountryCode = sessionCountryCode;
      let finalPaymentMethod = paymentMethod;
      let clientPhone = contribuable?.telephone;
      let paymentReference = "";
      let bank_name = "";
      
      switch (paymentMethod) {
        case "mobile_money":
          finalPaymentMethod = networkCode || paymentMethod;
          finalCountryCode = countryCode || sessionCountryCode;
          clientPhone = transactionRef.replace(/\s/g, '');
          paymentReference = transactionRef;
          bank_name = ``;
          break;
        case "credit_card":
          finalPaymentMethod = "credit_card";
          bank_name = ``;
          paymentReference = cardNumber.replace(/\s/g, '');
          break;
        case "check":
          finalPaymentMethod = "bank_check";
          bank_name = `${chequeBank}`;
          paymentReference = `${chequeNumber}`;
          break;
        case "bank_transfer":
          finalPaymentMethod = "bank_transfer";
          bank_name = `${transferBank}`;
          paymentReference = `${transferNumber}`;
          break;
        default:
          finalPaymentMethod = paymentMethod;
          bank_name = ``;
          paymentReference = "";
      }
      
      // IMPORTANT: Utiliser periodId (le paramètre d'URL) directement
      // car c'est l'ID d'échéance correct passé à la page
      const paymentData = {
        taxe_id: taxId,
        usager_id: contribuableId,
        echeance_id: periodId,  // ← CORRECTION: Utiliser le paramètre d'URL directement
        payment_method: finalPaymentMethod,
        country_code: finalCountryCode,
        
        client_phone: clientPhone?.split('-')[0] || clientPhone,
        bank_name: bank_name,
        payment_reference: paymentReference,
        partial_amount: paymentType === "partial" ? parseFloat(customAmount) : null,
        currency: selectedCurrency,
        converted_amount: selectedCurrency !== defaultCurrency 
          ? convertAmountSync(amount, defaultCurrency, selectedCurrency) 
          : amount
      };

      console.log("=== ENVOI PAIEMENT ===");
      console.log("echeance_id envoyé:", periodId);
      console.log("Payment Data:", JSON.stringify(paymentData, null, 2));
      console.log("======================");
      
      const apiResponse = await axios.post(
        `${API_BASE_URL}/payments`,
        paymentData,
        { headers: getHeaders() }
      );
      
      const response = apiResponse.data;
     
      if (response.code === 201 || response.code === 200) {
        const paiementData = response.data.paiement || {};
        const transactionData = {
          transactionId: paiementData.external_id,
          reference: paiementData.external_id,
          amount: parseFloat(paiementData.montant || amount),
          currency: selectedCurrency,
          paymentMethod: paymentMethod,
          paymentType: paymentType,
          partialAmount: paymentType === "partial" ? parseFloat(customAmount) : null,
          phoneNumber: paymentData.client_phone,
          contribuableId: contribuable?.contribuable_id,
          networkName: networkName,
          networkCode: networkCode,
          taxName: tax?.name,
          contribuableName: contribuable?.nom_contribuable,
          period: period?.period_label,
          montant_attendu: response.data.echeance?.montant_attendu,
          date_echeance: response.data.echeance?.date_echeance,
          echeanceId: periodId, 
          status: paiementData.statut || (paymentMethod === "mobile_money" ? "pending" : "completed"),
          timestamp: new Date().toISOString()
        };
        console.log("=== SAUVEGARDE TRANSACTION ===");
console.log("contribuableId:", contribuable?.contribuable_id);
console.log("transactionData complète:", response.data.paiement);

console.log("Période complète:", period);
localStorage.setItem('pendingTransaction', JSON.stringify(response.data.paiement));

        localStorage.setItem('pendingTransaction', JSON.stringify(transactionData));
        localStorage.setItem('transactionId', response.data.paiement.external_id);
        localStorage.setItem('periodLabel', period?.period_label);
        
        setShowConfirmModal(false);
        
        showToastMessage(` Collecte ${paymentType === 'partial' ? 'partielle' : 'complète'} effectuée avec succès !`, "success");
        setTimeout(() => {
          navigate(`/paiement-succes/${transactionData.reference}`);
        }, 1500);
      } else {
        showToastMessage(`❌ Erreur: ${response.message || "Échec du paiement"}`, "error");
      }
    } catch (apiError) {
      console.error('Erreur API paiement:', apiError);
      const errorMessage = apiError.response?.data?.message || apiError.message || "Erreur de connexion au serveur";
      showToastMessage(`❌ ${errorMessage}`, "error");
    } finally {
      setCollecting(false);
    }
  };

  const renderPaymentDetails = () => {
    switch (paymentMethod) {
      case "mobile_money":
        return (
          <>
            <Form.Label className="fw-semibold small text-uppercase mt-3">
              Numéro de téléphone <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup>
              <InputGroup.Text><PhoneVibrate /></InputGroup.Text>
              <Form.Control 
                type="tel" 
                placeholder="Ex: 077 XX XX XX"
                value={transactionRef} 
                onChange={(e) => {
                  const value = e.target.value;
                  setTransactionRef(value);
                  if (/^[0-9\s\+]+$/.test(value)) {
                    setNumeroPaiement(value);
                  }
                }}
                required
              />
              {rechercheLoading && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" />
                </InputGroup.Text>
              )}
            </InputGroup>
            
            {networkName && (
              <Alert variant="success" className="mt-2 py-2">
                <div className="d-flex align-items-center">
                  {networkLogo && (
                    <img src={networkLogo} alt={networkName} style={{ width: '24px', height: '24px', borderRadius: '50%' }} className="me-2" />
                  )}
                  <div className="flex-grow-1">
                    <span className="fw-semibold">{networkName}</span>
                    <span className="text-muted ms-1">({networkCode})</span>
                  </div>
                  <Badge bg="success"><CheckCircle size={10} className="me-1" />Détecté</Badge>
                </div>
              </Alert>
            )}
            
            {detectionError && transactionRef && (
              <Alert variant="warning" className="mt-2 py-1 px-2 small">
                <InfoCircle size={10} className="me-1" />
                {detectionError}
              </Alert>
            )}
          </>
        );
      
      case "credit_card":
        const formatCardNumber = (value) => {
          const cleaned = value.replace(/\s/g, '');
          const groups = cleaned.match(/.{1,4}/g);
          return groups ? groups.join(' ') : cleaned;
        };
        
        return (
          <>
            <Form.Label className="fw-semibold small text-uppercase mt-3">
              Numéro de carte bancaire <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup>
              <InputGroup.Text><CreditCard2Front /></InputGroup.Text>
              <Form.Control 
                type="text" 
                placeholder="XXXX XXXX XXXX XXXX"
                value={cardNumber} 
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/\s/g, '');
                  if (rawValue.length <= 16 && /^\d*$/.test(rawValue)) {
                    setCardNumber(formatCardNumber(rawValue));
                  }
                }}
                maxLength={19}
                required
              />
            </InputGroup>
            <Form.Text className="text-muted">
              Entrez le numéro à 16 chiffres de la carte
            </Form.Text>
          </>
        );
      
      case "check":
        return (
          <>
            <Form.Label className="fw-semibold small text-uppercase mt-3">
              Informations du chèque <span className="text-danger">*</span>
            </Form.Label>
            <Row>
              <Col md={6} className="mb-2">
                <InputGroup>
                  <InputGroup.Text><Bank2 /></InputGroup.Text>
                  <Form.Control 
                    type="text" 
                    placeholder="Banque"
                    value={chequeBank} 
                    onChange={(e) => setChequeBank(e.target.value)}
                    required
                  />
                </InputGroup>
              </Col>
              <Col md={6} className="mb-2">
                <InputGroup>
                  <InputGroup.Text><FileText /></InputGroup.Text>
                  <Form.Control 
                    type="text" 
                    placeholder="Numéro du chèque"
                    value={chequeNumber} 
                    onChange={(e) => setChequeNumber(e.target.value)}
                    required
                  />
                </InputGroup>
              </Col>
            </Row>
          </>
        );
      
      case "bank_transfer":
        return (
          <>
            <Form.Label className="fw-semibold small text-uppercase mt-3">
              Informations du virement <span className="text-danger">*</span>
            </Form.Label>
            <Row>
              <Col md={6} className="mb-2">
                <InputGroup>
                  <InputGroup.Text><Bank2 /></InputGroup.Text>
                  <Form.Control 
                    type="text" 
                    placeholder="Banque émettrice"
                    value={transferBank} 
                    onChange={(e) => setTransferBank(e.target.value)}
                    required
                  />
                </InputGroup>
              </Col>
              <Col md={6} className="mb-2">
                <InputGroup>
                  <InputGroup.Text><FileText /></InputGroup.Text>
                  <Form.Control 
                    type="text" 
                    placeholder="Numéro de virement"
                    value={transferNumber} 
                    onChange={(e) => setTransferNumber(e.target.value)}
                    required
                  />
                </InputGroup>
              </Col>
            </Row>
          </>
        );
      
      default:
        return null;
    }
  };

  const getPaymentStatus = (periodData) => {
    if (!periodData) return 'en_attente';
    const montantInitial = parseFloat(periodData.montant_initial || periodData.montant_du || 0);
    const montantPaye = parseFloat(periodData.montant_paye || 0);
    const percentage = montantInitial > 0 ? (montantPaye / montantInitial) * 100 : 100;
    
    if (percentage >= 100) return 'payee';
    else if (percentage > 0) return 'partiel';
    return 'en_attente';
  };

  const getPaymentStatusBadge = (periodData) => {
    const status = getPaymentStatus(periodData);
    const configs = {
      'en_attente': { label: 'En attente', color: 'warning', icon: <HourglassSplit className="me-1" /> },
      'payee': { label: 'Payée', color: 'success', icon: <CheckCircle className="me-1" /> },
      'partiel': { label: 'Paiement partiel', color: 'warning', icon: <Percent className="me-1" /> }
    };
    const config = configs[status];
    return (
      <Badge bg={config.color} className="px-3 py-2">
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const getDaysLate = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    if (due >= today) return 0;
    const diffTime = Math.abs(today - due);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  

if (loading || loadingPeriod) {
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
        <Container fluid>
          {/* En-tête squelette */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="placeholder-glow">
                    <span className="placeholder rounded-pill" style={{ height: '40px', width: '250px' }}></span>
                  </div>
                  <div className="placeholder-glow mt-2">
                    <span className="placeholder rounded-pill" style={{ height: '20px', width: '300px' }}></span>
                  </div>
                </div>
                <div className="placeholder-glow">
                  <span className="placeholder rounded-circle" style={{ width: '40px', height: '40px' }}></span>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            {/* Colonne gauche - Squelette des informations */}
            <Col lg={6}>
              {/* Carte principale squelette */}
              <Card className="border-0 shadow-lg mb-4">
                <div className="card-header-gradient bg-primary" style={{ height: '6px' }}></div>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start mb-4">
                    <div className="placeholder-glow">
                      <span className="placeholder rounded-pill" style={{ height: '24px', width: '150px' }}></span>
                      <div className="mt-2">
                        <span className="placeholder rounded-pill" style={{ height: '30px', width: '120px' }}></span>
                      </div>
                    </div>
                    <div className="text-end placeholder-glow">
                      <span className="placeholder rounded-pill" style={{ height: '16px', width: '80px' }}></span>
                      <div className="mt-1">
                        <span className="placeholder rounded-pill" style={{ height: '20px', width: '100px' }}></span>
                      </div>
                    </div>
                  </div>
                  
                  <Row className="g-4">
                    {[1, 2, 3, 4].map(i => (
                      <Col md={6} key={i}>
                        <div className="bg-light rounded p-3">
                          <div className="placeholder-glow">
                            <span className="placeholder rounded-pill d-block mb-2" style={{ height: '14px', width: '80px' }}></span>
                            <span className="placeholder rounded-pill d-block" style={{ height: '30px', width: '100px' }}></span>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                  
                  <div className="mt-4">
                    <div className="d-flex justify-content-between mb-2 placeholder-glow">
                      <span className="placeholder rounded-pill" style={{ height: '14px', width: '100px' }}></span>
                      <span className="placeholder rounded-pill" style={{ height: '14px', width: '40px' }}></span>
                    </div>
                    <div className="placeholder-glow">
                      <span className="placeholder w-100 rounded-pill" style={{ height: '10px' }}></span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
              
              {/* Carte contribuable squelette */}
              <Card className="border-0 shadow-lg mb-4">
                <Card.Body className="p-4">
                  <div className="placeholder-glow">
                    <span className="placeholder rounded-pill mb-3 d-block" style={{ height: '20px', width: '180px' }}></span>
                  </div>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3 placeholder-glow">
                        <span className="placeholder rounded-pill d-block mb-1" style={{ height: '12px', width: '80px' }}></span>
                        <span className="placeholder rounded-pill" style={{ height: '20px', width: '150px' }}></span>
                      </div>
                      <div className="mb-3 placeholder-glow">
                        <span className="placeholder rounded-pill d-block mb-1" style={{ height: '12px', width: '60px' }}></span>
                        <span className="placeholder rounded-pill" style={{ height: '20px', width: '120px' }}></span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3 placeholder-glow">
                        <span className="placeholder rounded-pill d-block mb-1" style={{ height: '12px', width: '70px' }}></span>
                        <span className="placeholder rounded-pill" style={{ height: '20px', width: '130px' }}></span>
                      </div>
                      <div className="mb-3 placeholder-glow">
                        <span className="placeholder rounded-pill d-block mb-1" style={{ height: '12px', width: '50px' }}></span>
                        <span className="placeholder rounded-pill" style={{ height: '20px', width: '180px' }}></span>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            
            {/* Colonne droite - Squelette du formulaire de paiement */}
            <Col lg={6}>
              <Card className="border-0 shadow-lg">
                <div className="card-header-gradient bg-success" style={{ height: '6px' }}></div>
                <Card.Body className="p-4">
                  <div className="placeholder-glow mb-4">
                    <span className="placeholder rounded-pill" style={{ height: '20px', width: '180px' }}></span>
                  </div>
                  
                  {/* Types de paiement squelette */}
                  <div className="mb-4">
                    <div className="placeholder-glow mb-2">
                      <span className="placeholder rounded-pill" style={{ height: '14px', width: '120px' }}></span>
                    </div>
                    <Row className="g-2">
                      {[1, 2].map(i => (
                        <Col sm={6} key={i}>
                          <div className="p-3 border rounded bg-light">
                            <div className="d-flex align-items-center">
                              <div className="bg-light rounded-circle p-2 me-3">
                                <span className="placeholder rounded-circle" style={{ width: '20px', height: '20px' }}></span>
                              </div>
                              <div className="flex-grow-1 placeholder-glow">
                                <span className="placeholder rounded-pill d-block mb-1" style={{ height: '16px', width: '80px' }}></span>
                                <span className="placeholder rounded-pill d-block" style={{ height: '12px', width: '120px' }}></span>
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                  
                  {/* Méthodes de paiement squelette */}
                  <div className="mb-4">
                    <div className="placeholder-glow mb-2">
                      <span className="placeholder rounded-pill" style={{ height: '14px', width: '140px' }}></span>
                    </div>
                    <div className="row g-2">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="col-lg-6 col-md-6 col-sm-12">
                          <div className="p-3 border rounded bg-light h-100">
                            <div className="d-flex align-items-center">
                              <div className="bg-light rounded-circle p-2 me-3">
                                <span className="placeholder rounded-circle" style={{ width: '20px', height: '20px' }}></span>
                              </div>
                              <div className="flex-grow-1 placeholder-glow">
                                <span className="placeholder rounded-pill d-block mb-1" style={{ height: '16px', width: '80px' }}></span>
                                <span className="placeholder rounded-pill d-block" style={{ height: '12px', width: '100px' }}></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Montant à collecter squelette */}
                  <div className="bg-light rounded p-3 mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="placeholder-glow">
                        <span className="placeholder rounded-pill d-block mb-1" style={{ height: '12px', width: '120px' }}></span>
                        <span className="placeholder rounded-pill" style={{ height: '32px', width: '100px' }}></span>
                      </div>
                      <div className="text-end placeholder-glow">
                        <span className="placeholder rounded-pill d-block mb-1" style={{ height: '12px', width: '60px' }}></span>
                        <span className="placeholder rounded-pill" style={{ height: '24px', width: '80px' }}></span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Boutons squelette */}
                  <div className="d-flex gap-3">
                    <div className="placeholder-glow flex-grow-1">
                      <span className="placeholder w-100 rounded-pill" style={{ height: '42px' }}></span>
                    </div>
                    <div className="placeholder-glow flex-grow-1">
                      <span className="placeholder w-100 rounded-pill" style={{ height: '42px' }}></span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
        <Footer />
      </div>
    </>
  );
}



  const montantRestant = period?.montant_restant || 0;
  const isFullyPaid = montantRestant === 0;
  const activityName = subAccount 
    ? getActivityName(subAccount.activite_contribuable)
    : (contribuable ? getActivityName(contribuable.activite_contribuable) : null);

 if (isFullyPaid) {
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Card className="border-0 shadow-xl text-center py-5 px-4 rounded-4" style={{ maxWidth: '550px', width: '100%' }}>
            <Card.Body>
              {/* Icône animée */}
              <div className="position-relative d-inline-block mb-4">
                <div className="pulse-animation mx-auto d-flex align-items-center justify-content-center" style={{ width: '120px', height: '120px', background: 'linear-gradient(135deg, #28a74520, #28a74510)', borderRadius: '50%' }}>
                  <CheckCircle size={70} className="text-success" style={{ filter: 'drop-shadow(0 4px 6px rgba(40, 167, 69, 0.2))' }} />
                </div>
                <div className="position-absolute top-0 start-0 w-100 h-100">
                  <div className="spinner-grow text-success position-absolute top-0 start-0" style={{ width: '12px', height: '12px', animationDelay: '0s' }} />
                  <div className="spinner-grow text-success position-absolute top-0 end-0" style={{ width: '12px', height: '12px', animationDelay: '0.3s' }} />
                  <div className="spinner-grow text-success position-absolute bottom-0 start-0" style={{ width: '12px', height: '12px', animationDelay: '0.6s' }} />
                  <div className="spinner-grow text-success position-absolute bottom-0 end-0" style={{ width: '12px', height: '12px', animationDelay: '0.9s' }} />
                </div>
              </div>
              
              <h2 className="mb-3 fw-bold text-success">Échéance déjà payée</h2>
              <p className="text-muted mb-4 px-3">
                Cette échéance a déjà été entièrement payée. Aucune collecte supplémentaire n'est possible.
              </p>
              
              <div className="d-flex justify-content-center gap-3">
                <Button 
                  variant="outline-secondary" 
                  onClick={() => navigate(-1)}
                  className="px-4 py-2 rounded-pill"
                >
                  <ArrowLeft className="me-2" size={16} /> Retour
                </Button>
                <Button 
                  variant="success" 
                  onClick={() => navigate('/contribuables/CollectesUsagers')}
                  className="px-4 py-2 rounded-pill"
                >
                  <CheckCircle className="me-2" size={16} /> Voir mes collectes
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Container>
        
        <style>{`
          .shadow-xl {
            box-shadow: 0 20px 35px -12px rgba(0, 0, 0, 0.15) !important;
          }
          .pulse-animation {
            animation: pulse 1.5s ease-in-out infinite;
          }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.9; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
        
        <Footer />
      </div>
    </>
  );
}

  return (
    <>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <ToastContainer position="top-end" className="p-3" style={{ 
            zIndex: 9999,
            position: 'fixed',
            top: '20px',
            right: '20px'
          }}>
            <Toast 
              show={showToast} 
              onClose={() => setShowToast(false)} 
              delay={5000} 
              autohide
              className={`border-0 shadow-lg ${
                toastType === "success" ? "bg-success text-white" :
                toastType === "error" ? "bg-danger text-white" : "bg-info text-white"
              }`}
            >
              <Toast.Header className="border-0">
                <strong className="me-auto">
                  {toastType === "success" ? "✅ Succès" : toastType === "error" ? "❌ Erreur" : "ℹ️ Information"}
                </strong>
              </Toast.Header>
              <Toast.Body>{toastMessage}</Toast.Body>
            </Toast>
          </ToastContainer>
          
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="d-flex align-items-center mb-3">
                    <Button 
                      variant="light" 
                      onClick={() => navigate(-1)}
                      className="me-3 rounded-circle"
                      style={{ width: '40px', height: '40px' }}
                    >
                      <ArrowLeft />
                    </Button>
                    <div>
                      <h1 className="h3 mb-1 text-gradient-primary">
                        <CashCoin className="me-2" />
                        Nouvelle collecte
                      </h1>
                      <p className="text-muted mb-0">
                        Effectuez le paiement d'une échéance
                        {subAccount && (
                          <Badge bg="info" className="ms-2">
                            <Briefcase className="me-1" size={12} />
                            {activityName}
                          </Badge>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6}>
              <Card className="border-0 shadow-lg mb-4">
                <div className="card-header-gradient bg-primary"></div>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                      <h4 className="mb-2">{period?.period_label}</h4>
                      <div className="d-flex gap-2 flex-wrap">
                        {getPaymentStatusBadge(period)}
                        <Badge bg={getDaysLate(period?.due_date) > 0 ? "danger" : "info"} className="px-3 py-2">
                          {getDaysLate(period?.due_date) > 0 
                            ? `En retard de ${getDaysLate(period?.due_date)} jours`
                            : `Échéance: ${formatDate(period?.due_date)}`}
                        </Badge>
                        {subAccount && (
                          <Badge bg="secondary" className="px-3 py-2">
                            <Briefcase className="me-1" size={12} />
                            {activityName}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-end">
                      <small className="text-muted">ID Référence</small>
                      <div><code className="fs-6">{periodId}</code></div>
                    </div>
                  </div>
                  
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="bg-light rounded p-3">
                        <small className="text-muted d-block mb-2">Montant initial</small>
                        <h3 className="mb-0">{formatCurrency(period?.montant_initial)}</h3>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="bg-light rounded p-3">
                        <small className="text-muted d-block mb-2">Déjà payé</small>
                        <h3 className="mb-0 text-success">{formatCurrency(period?.montant_paye)}</h3>
                      </div>
                    </Col>
                    {period?.interest > 0 && (
                      <Col md={6}>
                        <div className="bg-warning bg-opacity-10 rounded p-3 border border-warning">
                          <small className="text-warning d-block mb-2">Intérêts / Pénalités</small>
                          <h4 className="mb-0 text-warning">{formatCurrency(period?.interest)}</h4>
                        </div>
                      </Col>
                    )}
                    <Col md={period?.interest > 0 ? 6 : 12}>
                      <div className="bg-primary bg-opacity-10 rounded p-3 border border-primary">
                        <small className="text-primary d-block mb-2">Reste à payer</small>
                        <h2 className="mb-0 text-primary">{formatCurrency(period?.montant_restant)}</h2>
                      </div>
                    </Col>
                  </Row>
                  
                  <div className="mt-4">
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">Progression du paiement</small>
                      <small className="fw-bold">{period?.payment_percentage || 0}%</small>
                    </div>
                    <ProgressBar 
                      now={period?.payment_percentage || 0} 
                      variant={period?.payment_percentage === 100 ? "success" : "primary"}
                      className="rounded-pill"
                      style={{ height: '10px' }}
                    />
                  </div>
                </Card.Body>
              </Card>
              
              <Card className="border-0 shadow-lg mb-4">
                <Card.Body className="p-4">
                  <h5 className="mb-3">
                    <Receipt className="me-2" />
                    Informations contribuable
                  </h5>
                  <Row>
                    <Col md={6}>
                      <div className="mb-2">
                        <small className="text-muted d-block">Nom / Raison sociale</small>
                        <div className="fw-semibold d-flex align-items-center gap-2">
                          {contribuable?.nom_contribuable?.toLocaleUpperCase('fr-FR') || 'Non spécifié'}
                          {subAccount && (
                            <Badge bg="info" className="ms-2" style={{ fontSize: '10px' }}>
                              Activité secondaire
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">ID Contribuable</small>
                        <div><code>{contribuable?.contribuable_id || contribuable?.id || 'N/A'}</code></div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <small className="text-muted d-block">Téléphone</small>
                        <div>{contribuable?.telephone || 'Non spécifié'}</div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">Email</small>
                        <div>{contribuable?.email || 'Non spécifié'}</div>
                      </div>
                    </Col>
                  </Row>
                  
                  {subAccount && subAccount.main_account_id && (
                    <div className="mt-3 pt-3 border-top">
                      <small className="text-muted d-flex align-items-center gap-2">
                        <Building size={14} />
                        Compte principal: ID {subAccount.main_account_id}
                      </small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={6}>
              <Card className="border-0 shadow-lg" style={{ top: '0px' }}>
                <div className="card-header-gradient bg-success"></div>
                <Card.Body className="p-4">
                  <h5 className="mb-3">
                    <Cash className="me-2 text-success" />
                    Détails du paiement
                  </h5>
                  
                  <div className="mb-4">
                    <label className="form-label fw-semibold small text-uppercase">Type de collecte</label>
                    <div className="row g-2">
                      {paymentTypes.map((type) => (
                        <Col key={type.id} sm={6}>
                          <div 
                            className={`p-3 border rounded cursor-pointer transition-all ${
                              paymentType === type.id 
                                ? `border-${type.color} bg-${type.color}-subtle shadow-sm` 
                                : 'border-light bg-light'
                            }`}
                            onClick={() => {
                              setPaymentType(type.id);
                              if (type.id === "full") setCustomAmount("");
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div className={`bg-${type.color} bg-opacity-10 rounded-circle p-2 me-3`}>
                                {React.cloneElement(type.icon, { className: `text-${type.color}`, size: 20 })}
                              </div>
                              <div className="flex-grow-1">
                                <div className="fw-semibold">{type.label}</div>
                                <small className="text-muted">{type.description}</small>
                              </div>
                              {paymentType === type.id && (
                                <CheckCircle className={`text-${type.color}`} size={18} />
                              )}
                            </div>
                          </div>
                        </Col>
                      ))}
                    </div>
                  </div>
                  
                  {paymentType === "partial" && (
                    <div className="mb-4">
                      <label className="form-label fw-semibold small text-uppercase">
                        Montant à payer
                        <small className="text-muted ms-1">(Maximum: {formatCurrency(montantRestant)})</small>
                      </label>
                      <InputGroup>
                        <InputGroup.Text>{defaultCurrency}</InputGroup.Text>
                        <Form.Control 
                          type="number" 
                          value={customAmount} 
                          onChange={(e) => {
                            const value = e.target.value;
                            setCustomAmount(value);
                            const parsedAmount = parseFloat(value) || 0;
                            const maxAmount = montantRestant;
                            setAmount(Math.min(parsedAmount, maxAmount));
                          }}
                          min="0" 
                          max={montantRestant} 
                          placeholder="Saisissez le montant"
                          className="py-2"
                        />
                      </InputGroup>
                      <div className="d-flex justify-content-between mt-2">
                        <div className="d-flex gap-2">
                          <Button variant="outline-secondary" size="sm" onClick={() => setCustomAmount(Math.floor(montantRestant * 0.25).toString())}>25%</Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => setCustomAmount(Math.floor(montantRestant * 0.5).toString())}>50%</Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => setCustomAmount(Math.floor(montantRestant * 0.75).toString())}>75%</Button>
                        </div>
                        {customAmount && (
                          <small className={`fw-medium ${parseFloat(customAmount) > montantRestant ? 'text-danger' : 'text-success'}`}>
                            Nouveau reste: {formatCurrency(montantRestant - (parseFloat(customAmount) || 0))}
                          </small>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <label className="form-label fw-semibold small text-uppercase">Méthode de collecte</label>
                    <div className="row g-2">
                      {paymentMethods.map((method) => (
                        <div key={method.id} className="col-lg-6 col-md-6 col-sm-12">
                          <div 
                            className={`p-3 border rounded cursor-pointer transition-all h-100 ${
                              paymentMethod === method.id 
                                ? `border-${method.color} bg-${method.color}-subtle shadow-sm` 
                                : 'border-light bg-light'
                            }`}
                            onClick={() => setPaymentMethod(method.id)}
                          >
                            <div className="d-flex align-items-center">
                              <div className={`bg-${method.color} bg-opacity-10 rounded-circle p-2 me-3`}>
                                {React.cloneElement(method.icon, { className: `text-${method.color}`, size: 20 })}
                              </div>
                              <div className="flex-grow-1">
                                <div className="fw-semibold">{method.label}</div>
                                <small className="text-muted">{method.description}</small>
                              </div>
                              {paymentMethod === method.id && (
                                <CheckCircle className={`text-${method.color}`} size={18} />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {renderPaymentDetails()}
                  
                  {availableCurrencies.length > 1 && (
                    <div className="mb-4">
                      <label className="form-label fw-semibold small text-uppercase">
                        <CurrencyDollar className="me-1" size={14} />
                        Devise de paiement
                        {convertingCurrency && <Spinner animation="border" size="sm" className="ms-2" />}
                      </label>
                      <div className="d-flex gap-2 flex-wrap">
                        {availableCurrencies.map((currency) => {
                          const isSelected = selectedCurrency === currency.code;
                          const isDefaultCurrency = currency.code === defaultCurrency;
                          
                          let displayAmount = null;
                          if (isDefaultCurrency) {
                            displayAmount = amount;
                          } else if (isSelected && conversionRates[currency.code]) {
                            displayAmount = Math.round(amount * conversionRates[currency.code] * 100) / 100;
                          }
                          
                          return (
                            <div 
                              key={currency.code}
                              className="flex-grow-1"
                              style={{ cursor: 'pointer', minWidth: '120px' }}
                              onClick={async () => {
                                if (currency.code === selectedCurrency) return;
                                setConvertingCurrency(true);
                                try {
                                  setSelectedCurrency(currency.code);
                                  if (currency.code !== defaultCurrency) {
                                    await convertCurrency(defaultCurrency, currency.code, amount);
                                  }
                                } finally {
                                  setConvertingCurrency(false);
                                }
                              }}
                            >
                              <Card className={`border cursor-pointer transition-all h-100 ${
                                selectedCurrency === currency.code ? 'border-primary bg-primary-subtle' : 'border-light'
                              }`}>
                                <Card.Body className="p-2">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                      <div className="fw-bold">{currency.code}</div>
                                      <small className="text-muted" style={{ fontSize: '10px' }}>{currency.name}</small>
                                    </div>
                                    <div className="text-end">
                                      {displayAmount !== null ? (
                                        <div className="fw-bold" style={{ fontSize: '13px', color: isSelected ? '#0d6efd' : '#198754' }}>
                                          {displayAmount.toLocaleString('fr-FR')} {currency.code}
                                        </div>
                                      ) : (
                                        <div className="text-muted" style={{ fontSize: '11px' }}>Cliquer</div>
                                      )}
                                      {selectedCurrency === currency.code && (
                                        <CheckCircle size={12} className="text-success mt-1" />
                                      )}
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-primary bg-opacity-10 rounded p-3 mb-4 mt-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted d-block">Montant à collecter</small>
                        <h3 className="mb-0 text-primary">{formatCurrency(amount)}</h3>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block">Type</small>
                        <Badge bg={paymentType === "full" ? "success" : "warning"}>
                          {paymentType === "full" ? "Complet" : "Partiel"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-flex gap-3">
                    <Button variant="light" className="flex-grow-1 py-2" onClick={() => navigate(-1)} disabled={collecting}>Annuler</Button>
                    <Button variant="success" className="flex-grow-1 py-2" onClick={() => setShowConfirmModal(true)} disabled={collecting || (paymentType === "partial" && (!customAmount || parseFloat(customAmount) <= 0)) || !isPaymentDetailsValid()}>
                      {collecting ? <><Spinner animation="border" size="sm" className="me-2" />Traitement...</> : <><CheckCircle className="me-2" />Confirmer</>}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton className="bg-gradient-success text-white border-0">
          <Modal.Title className="d-flex align-items-center">
            <CheckCircle className="me-2" />Confirmer la collecte
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <div className="text-center mb-4">
            <div className="confirm-icon-circle mx-auto mb-3">
              {paymentType === "full" ? <CheckCircle size={48} className="text-success" /> : <Percent size={48} className="text-warning" />}
            </div>
            <h5>Confirmer la collecte {paymentType === "full" ? "complète" : "partielle"}</h5>
            <p className="text-muted mb-0">Vous êtes sur le point de collecter un paiement de</p>
            <h2 className={`my-3 ${paymentType === "full" ? "text-success" : "text-warning"}`}>{formatCurrency(amount)}</h2>
            {paymentType === "partial" && (
              <Alert variant="warning" className="mt-2">
                <ExclamationTriangle className="me-2" />
                {formatCurrency(montantRestant - amount)} restera à payer après cette transaction
              </Alert>
            )}
            {subAccount && (
              <Alert variant="info" className="mt-2">
                <Briefcase className="me-2" />
                Cette collecte concerne l'activité: <strong>{activityName}</strong>
              </Alert>
            )}
          </div>
          <Alert variant="warning" className="d-flex align-items-start">
            <InfoCircle className="me-2 mt-1 flex-shrink-0" />
            <div><strong>Attention:</strong> Cette action est irréversible. Assurez-vous d'avoir reçu le paiement avant de confirmer.</div>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setShowConfirmModal(false)} disabled={collecting}>Annuler</Button>
          <Button variant="success" onClick={confirmPayment} disabled={collecting}>
            {collecting ? <><Spinner animation="border" size="sm" className="me-2" />Traitement...</> : <><CheckCircle className="me-2" />Confirmer la collecte</>}
          </Button>
        </Modal.Footer>
      </Modal>
      
      <Footer />
      
      <style>{`
        .text-gradient-primary { background: linear-gradient(135deg, #0d6efd 0%, #6610f2 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .card-header-gradient { height: 6px; width: 100%; background: linear-gradient(90deg, #0d6efd 0%, #20c997 100%); }
        .bg-primary-subtle { background-color: rgba(13, 110, 253, 0.1) !important; }
        .bg-success-subtle { background-color: rgba(25, 135, 84, 0.1) !important; }
        .bg-warning-subtle { background-color: rgba(255, 193, 7, 0.1) !important; }
        .bg-info-subtle { background-color: rgba(13, 202, 240, 0.1) !important; }
        .bg-secondary-subtle { background-color: rgba(108, 117, 125, 0.1) !important; }
        .cursor-pointer { cursor: pointer; }
        .transition-all { transition: all 0.2s ease-in-out; }
        .confirm-icon-circle { width: 80px; height: 80px; background: rgba(25, 135, 84, 0.1); border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid rgba(25, 135, 84, 0.2); }
      `}</style>
    </>
  );
};

export default CollectePage;